import React, { ReactNode, useEffect } from "react";
import { GlobalStyles } from "@mui/material";
import {
  AnalyticsService,
  AnalyticsTrackingEvent,
  BRANDCLUB_BRAND_ID,
  useFingerprintJSContext,
} from "@brandclub/common-ui";
import { styled } from "@mui/material/styles";
import { AnimatePresence } from "framer-motion";
import CookiesProvider from "react-cookie/cjs/CookiesProvider";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
} from "react-router-dom";
import { LocationTrackingContextProvider } from "./providers/LocationTrackingProvider";

import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { useTrackViewEvent } from "../utils/hooks/useTracking";
import { updateVisitorTracking } from "../redux/reducers/visitorTracking";
import { StoreBrandingType } from "../types/misc";
import { removeFunctionFields } from "../utils/misc";
import ScrollToTop from "../utils/routing/ScrollToTop";
import { SiteProvider } from "./SiteProvider";
import AppDialogs from "./StoreComponents/StoreDialogs";
import StoreFullScreenLoading from "./StoreComponents/StoreFullScreenLoading";
import StoreSinglePageOutlet from "./StoreComponents/StoreSinglePageOutlet";
import { SyncProvider } from "./SyncProvider";
import AppMainPageOutlet from "./pages/AppPages/AppMainPageOutlet";
import AuthSignUpPageOutlet from "./pages/Auth/AuthSignUpPageOutlet";
import UserLoginProvider from "./pages/Auth/UserLoginProvider";
import {
  appPageRoutes,
  brandstoreLandingRoutes,
  dtcCommonAppPageRoutes,
  fallbackRoutes,
} from "./routes/appPageRoutes";
import { authRoutes } from "./routes/authRoutes";
import { dashboardRoutes, dtcDashboardRoutes } from "./routes/dashboardRoutes";
import { appGeneralRoutes, generalRoutes } from "./routes/generalRoutes";
import { buildRoutes } from "./routes/routeBuilder";
import {
  checkoutRoutes,
  dtcCheckoutRoutes,
  dtcSinglePageRoutes,
  membershipProgramRoutes,
  singlePageCustomCloseRoutes,
  singlePageRoutes,
} from "./routes/singlePageRoutes";
import { connectRoutes, syncRoutes, syncStatusRoutes } from "./routes/syncRoutes";
import DtcFooterOutlet from "./pages/AppPages/DtcFooterOutlet";
import { useCustomTheme } from "../utils/hooks/useCustomTheme";
import { AppSuspense } from "./AppSuspense";
import ThemeSetupOutlet from "./StoreComponents/ThemeSetupOutlet";
import { ConciergeContextProvider } from "./pages/AppPages/Concierge/ConciergeContextProvider";
import { ConciergeFloatingButton } from "./pages/AppPages/Concierge/ConciergeFloatingButton";
import { ConciergeWidget } from "./pages/AppPages/Concierge/ConciergeWidget";

const StyledMain = styled("main")(() => ({
  overflow: "hidden",
}));

const RouteProviders = () => {
  useCustomTheme();

  return (
    <ConciergeContextProvider>
      <ScrollToTop />
      <GlobalStyles
        styles={(theme) => ({
          "*": {
            fontFamily: `${theme.typography.fontFamily} !important`,
          },
        })}
      />
      <LocationTrackingContextProvider>
        <TrackPageViewContextProvider>
          <CookiesProvider>
            <UserLoginProvider>
              <SiteProvider>
                <SyncProvider>
                  <AppSuspense fallback={<StoreFullScreenLoading />}>
                    <AppDialogs />
                    <Outlet />
                    <ConciergeFloatingButton />
                    <ConciergeWidget />
                  </AppSuspense>
                </SyncProvider>
              </SiteProvider>
            </UserLoginProvider>
          </CookiesProvider>
        </TrackPageViewContextProvider>
      </LocationTrackingContextProvider>
    </ConciergeContextProvider>
  );
};

const UniversalStoreRoutes = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route element={<RouteProviders />}>
        {buildRoutes(generalRoutes)}
        {buildRoutes(fallbackRoutes)}
        <Route element={<StoreSinglePageOutlet />}>
          <Route element={<AuthSignUpPageOutlet />}>
            <Route>{buildRoutes(authRoutes)}</Route>
          </Route>
          {buildRoutes(singlePageCustomCloseRoutes)}
          {buildRoutes(singlePageRoutes)}
          {buildRoutes(checkoutRoutes)}
        </Route>

        <Route element={<AppMainPageOutlet />}>
          {/*app pages */}
          {buildRoutes(brandstoreLandingRoutes)}
          {buildRoutes(appGeneralRoutes)}
          {buildRoutes(appPageRoutes)}
          {buildRoutes(syncRoutes)}
          {buildRoutes(syncStatusRoutes)}
          {buildRoutes(dashboardRoutes)}
        </Route>
      </Route>
    </Route>
  )
);

const DtcRoutes = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route element={<RouteProviders />}>
        {buildRoutes(generalRoutes)}
        {buildRoutes(fallbackRoutes)}
        <Route element={<StoreSinglePageOutlet />}>
          <Route element={<DtcFooterOutlet />}>
            {buildRoutes(dtcDashboardRoutes)}
            {buildRoutes(dtcCommonAppPageRoutes)}
            {buildRoutes(syncRoutes)}
            {buildRoutes(syncStatusRoutes)}
            {buildRoutes(connectRoutes)}
            {buildRoutes(membershipProgramRoutes)}
          </Route>
          <Route element={<AuthSignUpPageOutlet />}>
            <Route>{buildRoutes(authRoutes)}</Route>
          </Route>
          {buildRoutes(dtcCheckoutRoutes)}
          {buildRoutes(dtcSinglePageRoutes)}
        </Route>
      </Route>
    </Route>
  )
);

const TrackPageViewContext = React.createContext<
  (data: { pageType: string; pageId: string }) => void
>(() => {});

export const useAnalyticsTrackPageView = (
  pageType?: string,
  pageId?: string,
  additionalData?: any
) => {
  const trackPageView = React.useContext(TrackPageViewContext);
  return () => {
    trackPageView({ pageType, pageId, ...additionalData });
  };
};
const TrackPageViewContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const trackPageView = useTrackViewEvent<{ pageType: string; pageId: string }>(
    AnalyticsTrackingEvent.PAGE_VIEW
  );
  return (
    <TrackPageViewContext.Provider value={trackPageView}>
      {children}
    </TrackPageViewContext.Provider>
  );
};

const RoutesSwitch = () => {
  const appConfig = useAppSelector(({ appConfig }) => appConfig);
  if (
    appConfig?.domainConfig.brandId !== BRANDCLUB_BRAND_ID &&
    appConfig?.domainConfig.storeBrandingType ===
      StoreBrandingType.CustomDTCStore
  ) {
    return (
      <AnimatePresence mode="wait">
        <RouterProvider router={DtcRoutes} />
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <RouterProvider router={UniversalStoreRoutes} />
    </AnimatePresence>
  );
};

const AppRoutes = () => {
  const visitorTracking = useFingerprintJSContext();
  const userProfile = useAppSelector((state) => state.userProfile);
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Mixpanel tracking
    try {
      if (!AnalyticsService.isMixpanelInitialized()) {
        AnalyticsService.initializeMixpanel();
      }
      AnalyticsService.setCurrentVisitorTracking(visitorTracking);
      AnalyticsService.setMixpanelUserIdentity(visitorTracking, userProfile!);
      if (visitorTracking?.uniqueVisitorId) {
        dispatch(updateVisitorTracking(removeFunctionFields(visitorTracking)));
      }
    } catch (e) {
      console.warn(e);
    }
  }, [dispatch, visitorTracking, userProfile]);

  return (
    <StyledMain>
      <RoutesSwitch />
    </StyledMain>
  );
};

export default AppRoutes;
